/**
 * Bank List Data
 * 
 * ข้อมูลธนาคารกลาง - แก้ไขที่นี่ที่เดียว
 * ใช้ได้ทั้ง UI และ API/Cloud Function
 * 
 * ID Format: ใช้ตัวเลข string ('1', '2', '3'...) ตาม legacy system
 * Image: ใช้ชื่อตามตัวเลข (1.png, 2.png...)
 * 
 * TODO: ในอนาคตจะดึงจาก API แทน
 */

// ============================================
// Types
// ============================================

export type CurrencyCode = 'THB' | 'LAK' | 'KHR' | 'USD' | 'MMK' | 'VND'

export interface BankInfo {
  id: string           // '1', '2', '3'... (ตัวเลข string)
  code: string         // short code (kbank, scb, etc.)
  name: string         // ชื่อธนาคาร (local language)
  nameEn: string       // English name
  image: string        // path to bank logo (1.png, 2.png...)
  color: string        // brand color (hex)
  bankId?: string      // Parse objectId (จาก BankList class)
  bank_id?: string     // Bank ID 3 หลัก (004, 014, 006...) สำหรับ match กับ SlipLog
  swiftCode?: string   // SWIFT/BIC code
  active: boolean      // is available
  aliases?: string[]   // ชื่ออื่นๆ สำหรับค้นหา
}

export interface CurrencyBanks {
  currency: CurrencyCode
  currencyName: string
  currencySymbol: string
  country: string
  countryFlag: string
  banks: BankInfo[]
}

// ============================================
// Bank Images Path
// ============================================

// Path สำหรับรูป bank - แยกตาม currency
// th/ = Thailand (1.png - 19.png)
// la/ = Laos (101.png - 108.png)
// kh/ = Cambodia (201.png - 208.png)
const BANK_IMAGE_BASE = '/images/banks'

// ============================================
// Thailand Banks (THB)
// ตาม jsonBank() จาก Angular - ใช้ id เป็นตัวเลข
// ============================================

export const THAILAND_BANKS: BankInfo[] = [
  {
    id: '1',
    code: 'kbank',
    name: 'ธนาคารกสิกรไทย',
    nameEn: 'Kasikorn Bank',
    image: `${BANK_IMAGE_BASE}/th/1.png`,
    color: '#138f2d',
    bankId: 'r77xxGboQF',
    bank_id: '004',
    swiftCode: 'KASITHBK',
    active: true,
  },
  {
    id: '2',
    code: 'scb',
    name: 'ธนาคารไทยพาณิชย์',
    nameEn: 'Siam Commercial Bank',
    image: `${BANK_IMAGE_BASE}/th/2.png`,
    color: '#4e2a82',
    bankId: '5ODZ1Zd6w1',
    bank_id: '014',
    swiftCode: 'SICOTHBK',
    active: true,
  },
  {
    id: '3',
    code: 'ktb',
    name: 'ธนาคารกรุงไทย',
    nameEn: 'Krungthai Bank',
    image: `${BANK_IMAGE_BASE}/th/3.png`,
    color: '#1ba5e0',
    bankId: 'K4d5OSRAP3',
    bank_id: '006',
    swiftCode: 'KRTHTHBK',
    active: true,
  },
  {
    id: '4',
    code: 'bbl',
    name: 'ธนาคารกรุงเทพ',
    nameEn: 'Bangkok Bank',
    image: `${BANK_IMAGE_BASE}/th/4.png`,
    color: '#1e4598',
    bankId: 'EgjlZ9A7xV',
    bank_id: '002',
    swiftCode: 'BKKBTHBK',
    active: true,
  },
  {
    id: '5',
    code: 'bay',
    name: 'ธนาคารกรุงศรีอยุธยา',
    nameEn: 'Bank of Ayudhya',
    image: `${BANK_IMAGE_BASE}/th/5.png`,
    color: '#fec43b',
    bankId: 'CjYbobn9BM',
    bank_id: '025',
    swiftCode: 'AYUDTHBK',
    active: true,
  },
  {
    id: '6',
    code: 'ttb',
    name: 'ธนาคารทหารไทยธนชาต',
    nameEn: 'TMBThanachart Bank',
    image: `${BANK_IMAGE_BASE}/th/6.png`,
    color: '#fc4f1f',
    bankId: 'UFn5bea8cF',
    bank_id: '011',
    swiftCode: 'TABOROMM',
    active: true,
  },
  {
    id: '7',
    code: 'gsb',
    name: 'ธนาคารออมสิน',
    nameEn: 'Government Savings Bank',
    image: `${BANK_IMAGE_BASE}/th/7.png`,
    color: '#eb198d',
    bankId: 'IjzUMPh6mm',
    bank_id: '030',
    swiftCode: 'GABOROMM',
    active: true,
  },
  {
    id: '8',
    code: 'ghb',
    name: 'ธนาคารอาคารสงเคราะห์',
    nameEn: 'Government Housing Bank',
    image: `${BANK_IMAGE_BASE}/th/8.png`,
    color: '#f57f20',
    bankId: 'XSXPqRqPBJ',
    bank_id: '033',
    swiftCode: 'GHBATHBK',
    active: true,
  },
  {
    id: '9',
    code: 'baac',
    name: 'ธนาคารเพื่อการเกษตรและสหกรณ์การเกษตร',
    nameEn: 'Bank for Agriculture and Agricultural Cooperatives',
    image: `${BANK_IMAGE_BASE}/th/9.png`,
    color: '#4b9b1d',
    bankId: 'R8lxeClqTg',
    bank_id: '034',
    swiftCode: 'BAABOROMM',
    active: true,
  },
  {
    id: '10',
    code: 'kkp',
    name: 'ธนาคารเกียรตินาคินภัทร',
    nameEn: 'Kiatnakin Phatra Bank',
    image: `${BANK_IMAGE_BASE}/th/10.png`,
    color: '#199cc5',
    bankId: 'ovl68iJUhx',
    bank_id: '069',
    swiftCode: 'KKPBTHBK',
    active: true,
  },
  {
    id: '11',
    code: 'tisco',
    name: 'ธนาคารทิสโก้',
    nameEn: 'TISCO Bank',
    image: `${BANK_IMAGE_BASE}/th/11.png`,
    color: '#12549f',
    bankId: 'DAx5AzMOVb',
    bank_id: '067',
    swiftCode: 'TFPCTHB1',
    active: true,
  },
  {
    id: '12',
    code: 'uob',
    name: 'ธนาคารยูโอบี',
    nameEn: 'United Overseas Bank',
    image: `${BANK_IMAGE_BASE}/th/12.png`,
    color: '#0b3979',
    bankId: 'BUsExSm4L7',
    bank_id: '024',
    swiftCode: 'UOVBTHBK',
    active: true,
  },
  {
    id: '13',
    code: 'cimb',
    name: 'ธนาคารซีไอเอ็มบีไทย',
    nameEn: 'CIMB Thai Bank',
    image: `${BANK_IMAGE_BASE}/th/13.png`,
    color: '#7e2f36',
    bankId: '7R7OlpI6jH',
    bank_id: '022',
    swiftCode: 'UBOBTHBK',
    active: true,
  },
  {
    id: '14',
    code: 'citi',
    name: 'ธนาคารซิตี้แบงก์',
    nameEn: 'Citibank',
    image: `${BANK_IMAGE_BASE}/th/14.png`,
    color: '#1583c7',
    bankId: 'gUNAa5jHNr',
    swiftCode: 'CITITHBX',
    active: false,
  },
  {
    id: '15',
    code: 'hsbc',
    name: 'ธนาคารฮ่องกงและเซี้ยงไฮ้',
    nameEn: 'HSBC',
    image: `${BANK_IMAGE_BASE}/th/15.png`,
    color: '#db0011',
    bankId: 'hT0VOy8buR',
    swiftCode: 'HSBCTHBK',
    active: false,
  },
  {
    id: '16',
    code: 'isbt',
    name: 'ธนาคารอิสลามแห่งประเทศไทย',
    nameEn: 'Islamic Bank of Thailand',
    image: `${BANK_IMAGE_BASE}/th/16.png`,
    color: '#0f6ea8',
    bankId: 'TGAmIQlENH',
    bank_id: '066',
    swiftCode: 'TIABTHBK',
    active: true,
  },
  {
    id: '17',
    code: 'icbc',
    name: 'ธนาคารไอซีบีซี',
    nameEn: 'ICBC Thailand',
    image: `${BANK_IMAGE_BASE}/th/17.png`,
    color: '#c50f1c',
    bankId: 'I4LaBZk4cR',
    bank_id: '070',
    swiftCode: 'ICBKTHBK',
    active: false,
  },
  {
    id: '18',
    code: 'lhbank',
    name: 'ธนาคารแลนด์ แอนด์ เฮ้าส์',
    nameEn: 'Land and Houses Bank',
    image: `${BANK_IMAGE_BASE}/th/18.png`,
    color: '#6d6e71',
    bankId: 'ZaqPKeDDAI',
    bank_id: '073',
    swiftCode: 'LAABOROMM',
    active: true,
  },
  {
    id: '19',
    code: 'truewallet',
    name: 'ทรูมันนี่วอลเล็ท',
    nameEn: 'TrueMoney Wallet',
    image: `${BANK_IMAGE_BASE}/th/19.png`,
    color: '#f68b1f',
    bankId: '71TEq4sZHb',
    bank_id: 'truewallet',
    active: true,
  },
]

// ============================================
// Laos Banks (LAK)
// TODO: อัพเดท id และ bankId ให้ตรงกับ Parse Server
// ============================================

export const LAOS_BANKS: BankInfo[] = [
  {
    id: '1',
    code: 'bcel',
    name: 'ທະນາຄານການຄ້າຕ່າງປະເທດລາວ',
    nameEn: 'BCEL',
    image: `${BANK_IMAGE_BASE}/la/1.png`,
    color: '#004b87',
    active: true,
  },
  {
    id: '2',
    code: 'ldb',
    name: 'ທະນາຄານພັດທະນາລາວ',
    nameEn: 'Lao Development Bank',
    image: `${BANK_IMAGE_BASE}/la/2.png`,
    color: '#0066b3',
    active: true,
  },
  {
    id: '3',
    code: 'apb',
    name: 'ທະນາຄານສົ່ງເສີມກະສິກຳ',
    nameEn: 'Agriculture Promotion Bank',
    image: `${BANK_IMAGE_BASE}/la/3.png`,
    color: '#00a651',
    active: true,
  },
  {
    id: '4',
    code: 'jdb',
    name: 'ທະນາຄານຮ່ວມພັດທະນາ',
    nameEn: 'Joint Development Bank',
    image: `${BANK_IMAGE_BASE}/la/4.png`,
    color: '#ed1c24',
    active: true,
  },
  {
    id: '5',
    code: 'lvb',
    name: 'ທະນາຄານລາວ-ຫວຽດ',
    nameEn: 'Lao-Viet Bank',
    image: `${BANK_IMAGE_BASE}/la/5.png`,
    color: '#00529b',
    active: true,
  },
  {
    id: '6',
    code: 'stb',
    name: 'ທະນາຄານເອັສທີ',
    nameEn: 'Sacombank Lao',
    image: `${BANK_IMAGE_BASE}/la/6.png`,
    color: '#0066b3',
    active: true,
  },
  {
    id: '7',
    code: 'bfl',
    name: 'ທະນາຄານBFL',
    nameEn: 'Banque Franco-Lao',
    image: `${BANK_IMAGE_BASE}/la/7.png`,
    color: '#003366',
    active: true,
  },
  {
    id: '8',
    code: 'icb',
    name: 'ທະນາຄານอินโดจีນ',
    nameEn: 'Indochina Bank',
    image: `${BANK_IMAGE_BASE}/la/8.png`,
    color: '#1a4480',
    active: true,
  },
]

// ============================================
// Cambodia Banks (KHR)
// TODO: อัพเดท id และ bankId ให้ตรงกับ Parse Server
// ============================================

export const CAMBODIA_BANKS: BankInfo[] = [
  {
    id: '1',
    code: 'aba',
    name: 'ធនាគារ ABA',
    nameEn: 'ABA Bank',
    image: `${BANK_IMAGE_BASE}/kh/1.png`,
    color: '#005bab',
    active: true,
  },
  {
    id: '2',
    code: 'acleda',
    name: 'ធនាគារ អេស៊ីលីដា',
    nameEn: 'ACLEDA Bank',
    image: `${BANK_IMAGE_BASE}/kh/2.png`,
    color: '#00529b',
    active: true,
  },
  {
    id: '3',
    code: 'wing',
    name: 'វីង',
    nameEn: 'Wing Bank',
    image: `${BANK_IMAGE_BASE}/kh/3.png`,
    color: '#f7941d',
    active: true,
  },
  {
    id: '4',
    code: 'canadia',
    name: 'ធនាគារ កាណាឌីយ៉ា',
    nameEn: 'Canadia Bank',
    image: `${BANK_IMAGE_BASE}/kh/4.png`,
    color: '#c8102e',
    active: true,
  },
  {
    id: '5',
    code: 'prince',
    name: 'ធនាគារ ព្រីនស៍',
    nameEn: 'Prince Bank',
    image: `${BANK_IMAGE_BASE}/kh/5.png`,
    color: '#1a1a6c',
    active: true,
  },
  {
    id: '6',
    code: 'ppb',
    name: 'ធនាគារ ភ្នំពេញពាណិជ្ជ',
    nameEn: 'Phnom Penh Commercial Bank',
    image: `${BANK_IMAGE_BASE}/kh/6.png`,
    color: '#003366',
    active: true,
  },
  {
    id: '7',
    code: 'cpb',
    name: 'ធនាគារ កម្ពុជាសាធារណៈ',
    nameEn: 'Cambodia Public Bank',
    image: `${BANK_IMAGE_BASE}/kh/7.png`,
    color: '#ed1c24',
    active: true,
  },
  {
    id: '8',
    code: 'truemoney',
    name: 'ត្រូមណី',
    nameEn: 'TrueMoney Cambodia',
    image: `${BANK_IMAGE_BASE}/kh/8.png`,
    color: '#f68b1f',
    active: true,
  },
]

// ============================================
// All Banks by Currency
// ============================================

export const BANKS_BY_CURRENCY: CurrencyBanks[] = [
  {
    currency: 'THB',
    currencyName: 'Thai Baht',
    currencySymbol: '฿',
    country: 'Thailand',
    countryFlag: '🇹🇭',
    banks: THAILAND_BANKS,
  },
  {
    currency: 'LAK',
    currencyName: 'Lao Kip',
    currencySymbol: '₭',
    country: 'Laos',
    countryFlag: '🇱🇦',
    banks: LAOS_BANKS,
  },
  {
    currency: 'KHR',
    currencyName: 'Cambodian Riel',
    currencySymbol: '៛',
    country: 'Cambodia',
    countryFlag: '🇰🇭',
    banks: CAMBODIA_BANKS,
  },
  {
    currency: 'USD',
    currencyName: 'US Dollar',
    currencySymbol: '$',
    country: 'International',
    countryFlag: '🌐',
    banks: [], // ใช้ร่วมกับ currency อื่น
  },
]

// ============================================
// Helper Functions
// ============================================

/**
 * Get banks by currency code
 */
export function getBanksByCurrency(currency: CurrencyCode): BankInfo[] {
  const currencyBanks = BANKS_BY_CURRENCY.find(c => c.currency === currency)
  return currencyBanks?.banks || []
}

/**
 * Get active banks only
 */
export function getActiveBanks(currency: CurrencyCode): BankInfo[] {
  return getBanksByCurrency(currency).filter(b => b.active)
}

/**
 * Get bank by id (ตัวเลข string: '1', '2', '3'...)
 */
export function getBankById(id: string): BankInfo | undefined {
  for (const currencyBanks of BANKS_BY_CURRENCY) {
    const bank = currencyBanks.banks.find(b => b.id === id)
    if (bank) return bank
  }
  return undefined
}

/**
 * Smart bank lookup - ค้นหาธนาคารได้หลายวิธี (ค้นหาทุก currency)
 * รองรับ: id ('1','2'), code ('ktb','scb'), name ('ธนาคารกรุงไทย')
 * 
 * ⚠️ ใช้ findBankByCurrency() แทนถ้าต้องการค้นหาเฉพาะ currency
 */
export function findBank(value: string): BankInfo | undefined {
  if (!value) return undefined
  
  const searchValue = value.toLowerCase().trim()
  
  for (const currencyBanks of BANKS_BY_CURRENCY) {
    for (const bank of currencyBanks.banks) {
      // 1. Match by id (เลข string)
      if (bank.id === value) return bank
      
      // 2. Match by code (kbank, scb, ktb)
      if (bank.code.toLowerCase() === searchValue) return bank
      
      // 3. Match by bankId (Parse objectId)
      if (bank.bankId && bank.bankId === value) return bank
      
      // 4. Match by name (partial match)
      if (bank.name.toLowerCase().includes(searchValue)) return bank
      if (bank.nameEn.toLowerCase().includes(searchValue)) return bank
      
      // 5. Match by common aliases
      const aliases: Record<string, string[]> = {
        'kbank': ['กสิกร', 'kasikorn'],
        'scb': ['ไทยพาณิชย์', 'siam commercial'],
        'ktb': ['กรุงไทย', 'krungthai'],
        'bbl': ['กรุงเทพ', 'bangkok bank'],
        'bay': ['กรุงศรี', 'ayudhya'],
        'ttb': ['ทหารไทย', 'ธนชาต', 'tmb', 'thanachart'],
        'gsb': ['ออมสิน', 'government savings'],
        'truewallet': ['ทรู', 'true', 'วอลเล็ท', 'wallet'],
      }
      
      const bankAliases = aliases[bank.code] || []
      if (bankAliases.some(alias => searchValue.includes(alias.toLowerCase()))) {
        return bank
      }
    }
  }
  
  return undefined
}

/**
 * Smart bank lookup by currency - ค้นหาธนาคารเฉพาะ currency ที่ระบุ
 * รองรับ: id ('1','2'), code ('ktb','scb'), name ('ธนาคารกรุงไทย')
 * 
 * @param value - ค่าที่ใช้ค้นหา
 * @param currency - สกุลเงิน (THB, LAK, KHR)
 */
export function findBankByCurrency(value: string, currency: CurrencyCode): BankInfo | undefined {
  if (!value) return undefined
  
  const banks = getBanksByCurrency(currency)
  const searchValue = value.toLowerCase().trim()
  
  for (const bank of banks) {
    // 1. Match by id (เลข string '1', '2')
    if (bank.id === value) return bank
    
    // 2. Match by code (kbank, scb, ktb)
    if (bank.code.toLowerCase() === searchValue) return bank
    
    // 3. Match by bankId (Parse objectId)
    if (bank.bankId && bank.bankId === value) return bank
    
    // 4. Match by bank_id (เลข 3 หลัก '004', '014', '006'...)
    if (bank.bank_id && bank.bank_id === value) return bank
    
    // 5. Match by name (partial match)
    if (bank.name.toLowerCase().includes(searchValue)) return bank
    if (bank.nameEn?.toLowerCase().includes(searchValue)) return bank
  }
  
  return undefined
}

/**
 * Get bank by Parse objectId (bankId)
 */
export function getBankByBankId(bankId: string): BankInfo | undefined {
  for (const currencyBanks of BANKS_BY_CURRENCY) {
    const bank = currencyBanks.banks.find(b => b.bankId === bankId)
    if (bank) return bank
  }
  return undefined
}

/**
 * Get bank by code and currency
 */
export function getBankByCode(code: string, currency: CurrencyCode): BankInfo | undefined {
  return getBanksByCurrency(currency).find(b => b.code === code)
}

/**
 * Get all currencies
 */
export function getAllCurrencies(): CurrencyBanks[] {
  return BANKS_BY_CURRENCY
}

/**
 * Get currency info
 */
export function getCurrencyInfo(currency: CurrencyCode): Omit<CurrencyBanks, 'banks'> | undefined {
  const currencyData = BANKS_BY_CURRENCY.find(c => c.currency === currency)
  if (!currencyData) return undefined
  
  const { banks, ...info } = currencyData
  return info
}

/**
 * Search banks by name
 */
export function searchBanks(query: string, currency?: CurrencyCode): BankInfo[] {
  const searchTerm = query.toLowerCase()
  
  const banks = currency 
    ? getBanksByCurrency(currency)
    : BANKS_BY_CURRENCY.flatMap(c => c.banks)
  
  return banks.filter(b => 
    b.name.toLowerCase().includes(searchTerm) ||
    b.nameEn.toLowerCase().includes(searchTerm) ||
    b.code.toLowerCase().includes(searchTerm)
  )
}

// ============================================
// For API/Cloud Function Export
// ============================================

/**
 * Export all data for API
 */
export function exportForAPI() {
  return {
    version: '1.0.0',
    updatedAt: new Date().toISOString(),
    currencies: BANKS_BY_CURRENCY,
  }
}

// ============================================
// Legacy Format (ตรงกับ Angular jsonBank())
// ใช้สำหรับ compatibility กับระบบเดิม
// ============================================

/**
 * Get banks in legacy format (ตรงกับ Angular jsonBank())
 */
export function getLegacyBankList(currency: CurrencyCode = 'THB') {
  return getBanksByCurrency(currency).map(bank => ({
    id: bank.id,
    name: bank.name,
    image: bank.image,
    bank_id: bank.bankId || '',
  }))
}

// ============================================
// User bank_name Helper Functions
// ใน User class มี field bank_name ที่เก็บเป็นตัวเลข string
// เช่น '1' = กสิกร, '2' = ไทยพาณิชย์
// ============================================

/**
 * Get bank info from User's bank_name field
 * 
 * @param bankName - ค่า bank_name จาก User (เลข string เช่น '1', '2')
 * @param currency - สกุลเงิน (default: THB)
 * @returns BankInfo หรือ undefined ถ้าไม่เจอ
 * 
 * @example
 * const bank = getBankFromUserBankName('1') // ธนาคารกสิกร
 * const bank = getBankFromUserBankName('2') // ธนาคารไทยพาณิชย์
 */
export function getBankFromUserBankName(bankName: string, currency: CurrencyCode = 'THB'): BankInfo | undefined {
  if (!bankName) return undefined
  return getBanksByCurrency(currency).find(b => b.id === bankName)
}

/**
 * Get bank image URL from User's bank_name
 * 
 * @example
 * getBankImageFromUserBankName('1') // '/images/banks/1.png'
 */
export function getBankImageFromUserBankName(bankName: string, currency: CurrencyCode = 'THB'): string {
  const bank = getBankFromUserBankName(bankName, currency)
  return bank?.image || '/images/banks/default.png'
}

/**
 * Get bank display name from User's bank_name
 * 
 * @example
 * getBankNameFromUserBankName('1') // 'ธนาคารกสิกรไทย'
 */
export function getBankNameFromUserBankName(bankName: string, currency: CurrencyCode = 'THB'): string {
  const bank = getBankFromUserBankName(bankName, currency)
  return bank?.name || 'ไม่ระบุธนาคาร'
}

/**
 * Get bank color from User's bank_name
 * 
 * @example
 * getBankColorFromUserBankName('1') // '#138f2d' (สีเขียว KBANK)
 */
export function getBankColorFromUserBankName(bankName: string, currency: CurrencyCode = 'THB'): string {
  const bank = getBankFromUserBankName(bankName, currency)
  return bank?.color || '#6b7280' // default gray
}

/**
 * Format User bank info for display
 * 
 * @example
 * formatUserBankInfo('1', '123-4-56789-0')
 * // { bankName: 'ธนาคารกสิกรไทย', image: '/images/banks/1.png', color: '#138f2d', accountNumber: '123-4-56789-0' }
 */
export function formatUserBankInfo(bankName: string, bankAccount: string, currency: CurrencyCode = 'THB') {
  const bank = getBankFromUserBankName(bankName, currency)
  
  return {
    bankId: bankName,
    bankName: bank?.name || 'ไม่ระบุธนาคาร',
    bankNameEn: bank?.nameEn || 'Unknown Bank',
    bankCode: bank?.code || '',
    image: bank?.image || '/images/banks/default.png',
    color: bank?.color || '#6b7280',
    accountNumber: bankAccount || '',
  }
}

// ============================================
// COMMENTED OUT - Old format with prefixed IDs
// เก็บไว้ reference ถ้าต้องการกลับไปใช้
// ============================================

/*
// Old format with 'th-', 'la-', 'kh-' prefixed IDs
export const THAILAND_BANKS_OLD: BankInfo[] = [
  {
    id: 'th-kbank',
    code: 'kbank',
    name: 'ธนาคารกสิกรไทย',
    nameEn: 'Kasikorn Bank',
    image: `${BANK_IMAGE_BASE}/th/kbank.png`,
    color: '#138f2d',
    swiftCode: 'KASITHBK',
    active: true,
  },
  // ... more banks
]
*/
